import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../api/axiosConfig';
import './YoyComparisonTab.css';

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];
const MONTH_NAMES = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const fmt = (n) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.round(n ?? 0));
const fmtPct = (n) => {
    if (n === null || n === undefined) return '—';
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toFixed(1)}%`;
};
const fmtPctPlain = (n) => {
    if (n === null || n === undefined) return '—';
    return `${n.toFixed(1)}%`;
};
const fmtPp = (n) => {
    if (n === null || n === undefined) return '—';
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toFixed(1)} pp`;
};

const YoyComparisonTab = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [organizations, setOrganizations] = useState([]);
    const [organizationId, setOrganizationId] = useState('');
    const [year, setYear] = useState(CURRENT_YEAR);
    const [data, setData] = useState(null);
    const [mode, setMode] = useState('performance'); // 'performance' | 'revenue'

    useEffect(() => {
        const loadOrgs = async () => {
            try {
                const res = await axiosInstance.get('/api/sales-target/organizations/tree');
                if (res.data.success) {
                    const list = [];
                    const walk = (nodes, depth) => nodes.forEach(n => {
                        if (n.type === 'root' || n.type === 'company') {
                            list.push({ ...n, depth });
                        }
                        if (n.children) walk(n.children, depth + 1);
                    });
                    walk(res.data.data || [], 0);
                    setOrganizations(list);
                    if (!organizationId && list.length > 0) {
                        setOrganizationId(list[0]._id);
                    }
                }
            } catch (e) { console.error(e); }
        };
        loadOrgs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchData = useCallback(async () => {
        if (!organizationId) return;
        try {
            setLoading(true);
            setError(null);
            const res = await axiosInstance.get('/api/sales-target/analytics/yoy-comparison', {
                params: { organizationId, year }
            });
            if (res.data.success) setData(res.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load YoY data');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [organizationId, year]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleExport = async () => {
        if (!organizationId) return;
        try {
            const res = await axiosInstance.get('/api/sales-target/analytics/yoy-comparison/export-excel', {
                params: { organizationId, year, mode }, responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            const orgName = data?.organization?.name || 'Org';
            link.download = `YoY_${mode}_${(year - 1)}_vs_${year}_${orgName}.xlsx`;
            document.body.appendChild(link); link.click(); link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert('Failed to export to Excel');
        }
    };

    const pctClass = (v) => {
        const n = v ?? 0;
        if (n > 0) return 'pos';
        if (n < 0) return 'neg';
        return '';
    };

    return (
        <div className="yoy-tab">
            <div className="yoy-controls">
                <div className="yoy-filter">
                    <label>Organization</label>
                    <select value={organizationId} onChange={(e) => setOrganizationId(e.target.value)}>
                        <option value="">-- Select --</option>
                        {organizations.map(o => (
                            <option key={o._id} value={o._id}>
                                {'  '.repeat(o.depth)}{o.type === 'root' ? '🏢 ' : '🏬 '}{o.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="yoy-filter">
                    <label>Compare Year</label>
                    <select value={year} onChange={(e) => setYear(parseInt(e.target.value))}>
                        {YEARS.map(y => <option key={y} value={y}>{y} vs {y - 1}</option>)}
                    </select>
                </div>
                <div className="yoy-filter">
                    <label>Compare By</label>
                    <div className="yoy-mode-toggle">
                        <button
                            className={`yoy-mode-btn ${mode === 'performance' ? 'active' : ''}`}
                            onClick={() => setMode('performance')}
                            title="Compare target achievement % (recommended — neutralizes price/inflation effects)"
                        >Performance</button>
                        <button
                            className={`yoy-mode-btn ${mode === 'revenue' ? 'active' : ''}`}
                            onClick={() => setMode('revenue')}
                            title="Compare raw revenue (affected by prices and inflation)"
                        >Revenue</button>
                    </div>
                </div>
                <button className="yoy-btn-export" onClick={handleExport} disabled={loading || !data}>
                    📊 Export Excel
                </button>
                <button className="yoy-btn-refresh" onClick={fetchData} disabled={loading}>↻ Refresh</button>
            </div>

            {error && <div className="yoy-error">⚠ {error}</div>}
            {loading && <div className="yoy-loading">Loading…</div>}

            {data && !loading && (
                <>
                    <div className="yoy-header-bar">
                        <div className="yoy-title">
                            <span className="yoy-years">{data.previousYear} vs {data.year}</span>
                            <span className="yoy-org">— {data.organization.name}</span>
                        </div>
                        {data.groups.length > 0 && mode === 'performance' && (
                            <div className="yoy-summary">
                                <span>{data.previousYear} Ach: <strong>{fmtPctPlain(data.groups[0].totals.previousAchPct)}</strong></span>
                                <span>{data.year} Ach: <strong>{fmtPctPlain(data.groups[0].totals.currentAchPct)}</strong></span>
                                <span className={`yoy-growth ${pctClass(data.groups[0].totals.performanceDelta)}`}>
                                    Performance Δ: <strong>{fmtPp(data.groups[0].totals.performanceDelta)}</strong>
                                </span>
                            </div>
                        )}
                        {data.groups.length > 0 && mode === 'revenue' && (
                            <div className="yoy-summary">
                                <span>Total {data.previousYear}: <strong>{fmt(data.groups[0].totals.previous)}</strong></span>
                                <span>Total {data.year}: <strong>{fmt(data.groups[0].totals.current)}</strong></span>
                                <span className={`yoy-growth ${pctClass(data.groups[0].totals.growthPct)}`}>
                                    YoY: <strong>{fmtPct(data.groups[0].totals.growthPct)}</strong>
                                </span>
                            </div>
                        )}
                    </div>

                    {data.groups.length === 0 ? (
                        <div className="yoy-empty">No branches under this organization.</div>
                    ) : mode === 'performance' ? (
                        <div className="yoy-table-wrap">
                            <table className="yoy-table">
                                <thead>
                                    <tr>
                                        <th rowSpan={2} className="yoy-month-h">Month</th>
                                        {data.groups.map(g => (
                                            <th key={g.name} colSpan={7} className="yoy-group-h">{g.name}</th>
                                        ))}
                                    </tr>
                                    <tr>
                                        {data.groups.map(g => (
                                            <React.Fragment key={`sub-${g.name}`}>
                                                <th>{data.previousYear} Sales</th>
                                                <th>{data.previousYear} Tgt</th>
                                                <th>{data.previousYear} Ach%</th>
                                                <th>{data.year} Sales</th>
                                                <th>{data.year} Tgt</th>
                                                <th>{data.year} Ach%</th>
                                                <th>Δ (pp)</th>
                                            </React.Fragment>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.from({ length: 12 }, (_, i) => (
                                        <tr key={i}>
                                            <td className="yoy-month">{MONTH_NAMES[i]}</td>
                                            {data.groups.map(g => {
                                                const m = g.monthly[i];
                                                return (
                                                    <React.Fragment key={`${g.name}-${i}`}>
                                                        <td className="yoy-num">{fmt(m.previous)}</td>
                                                        <td className="yoy-num yoy-target">{fmt(m.previousTarget)}</td>
                                                        <td className="yoy-num yoy-pct">{fmtPctPlain(m.previousAchPct)}</td>
                                                        <td className="yoy-num yoy-current">{fmt(m.current)}</td>
                                                        <td className="yoy-num yoy-target">{fmt(m.currentTarget)}</td>
                                                        <td className="yoy-num yoy-pct">{fmtPctPlain(m.currentAchPct)}</td>
                                                        <td className={`yoy-num yoy-pp ${pctClass(m.performanceDelta)}`}>{fmtPp(m.performanceDelta)}</td>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                    <tr className="yoy-total">
                                        <td>TOTAL</td>
                                        {data.groups.map(g => (
                                            <React.Fragment key={`tot-${g.name}`}>
                                                <td className="yoy-num">{fmt(g.totals.previous)}</td>
                                                <td className="yoy-num">{fmt(g.totals.previousTarget)}</td>
                                                <td className="yoy-num yoy-pct">{fmtPctPlain(g.totals.previousAchPct)}</td>
                                                <td className="yoy-num">{fmt(g.totals.current)}</td>
                                                <td className="yoy-num">{fmt(g.totals.currentTarget)}</td>
                                                <td className="yoy-num yoy-pct">{fmtPctPlain(g.totals.currentAchPct)}</td>
                                                <td className={`yoy-num yoy-pp ${pctClass(g.totals.performanceDelta)}`}>{fmtPp(g.totals.performanceDelta)}</td>
                                            </React.Fragment>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="yoy-table-wrap">
                            <table className="yoy-table">
                                <thead>
                                    <tr>
                                        <th rowSpan={2} className="yoy-month-h">Month</th>
                                        {data.groups.map(g => (
                                            <th key={g.name} colSpan={4} className="yoy-group-h">{g.name}</th>
                                        ))}
                                    </tr>
                                    <tr>
                                        {data.groups.map(g => (
                                            <React.Fragment key={`sub-${g.name}`}>
                                                <th>{data.previousYear}</th>
                                                <th>{data.year}</th>
                                                <th>YoY %</th>
                                                <th>YTD %</th>
                                            </React.Fragment>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.from({ length: 12 }, (_, i) => (
                                        <tr key={i}>
                                            <td className="yoy-month">{MONTH_NAMES[i]}</td>
                                            {data.groups.map(g => {
                                                const m = g.monthly[i];
                                                return (
                                                    <React.Fragment key={`${g.name}-${i}`}>
                                                        <td className="yoy-num">{fmt(m.previous)}</td>
                                                        <td className="yoy-num yoy-current">{fmt(m.current)}</td>
                                                        <td className={`yoy-num yoy-pct ${pctClass(m.growthPct)}`}>{fmtPct(m.growthPct)}</td>
                                                        <td className={`yoy-num yoy-pct ${pctClass(m.ytdGrowthPct)}`}>{fmtPct(m.ytdGrowthPct)}</td>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                    <tr className="yoy-total">
                                        <td>TOTAL</td>
                                        {data.groups.map(g => (
                                            <React.Fragment key={`tot-${g.name}`}>
                                                <td className="yoy-num">{fmt(g.totals.previous)}</td>
                                                <td className="yoy-num">{fmt(g.totals.current)}</td>
                                                <td className={`yoy-num yoy-pct ${pctClass(g.totals.growthPct)}`}>{fmtPct(g.totals.growthPct)}</td>
                                                <td></td>
                                            </React.Fragment>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            {!data && !loading && !error && organizationId === '' && (
                <div className="yoy-info">Please select an organization to compare years.</div>
            )}
        </div>
    );
};

export default YoyComparisonTab;
